import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { Link } from "react-router-dom";
import "./about.css";

/* ------------------ Animations ------------------ */
const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: [0.25, 0.46, 0.45, 0.94] },
  },
};
const stagger = {
  visible: { transition: { staggerChildren: 0.15 } },
};
/* ------------------ Section Wrapper ------------------ */
function Section({ children, className = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <motion.section
      ref={ref}
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      variants={stagger}
      className={className}
    >
      {children}
    </motion.section>
  );
}
/* ------------------ Content ------------------ */

const beliefs = [
  "Great leaders leave digital footprints across the open web.",
  "Signal beats noise.",
  "Context beats keywords.",
  "And leadership hiring should feel like insight — not admin work.",
];

const capabilities = [
  { label: "Who they are", desc: "Beyond the headline" },
  { label: "What they're capable of", desc: "Competencies inferred from real work" },
  { label: "Where they thrive", desc: "Scale vs stability indicators" },
  { label: "How mobile they might be", desc: "Career velocity & transition patterns" },
  { label: "What they could do next", desc: "Hypothetical role-fit use cases" },
];

/* ------------------ Page ------------------ */

export default function About() {
  return (
    <div className="about-root">

        {/* HEADER */}
      <header className="about-header">
        <div className="header-inner">
          <Link to="/" className="logo" style={{ textDecoration: 'none', color: 'inherit' }}>
            Get<span className="gradient-text">lista</span>
          </Link>

          <nav className="header-nav">
            <span className="nav-item active">About Us</span>
          </nav>
        </div>
      </header>

      {/* HERO */}
      <Section className="hero">
        <motion.div variants={fadeUp} className="line-accent" />
        <motion.h1 variants={fadeUp}>
          Hiring leaders shouldn't feel like{" "}
          <span className="gradient-text">treasure hunting</span> in the dark.
        </motion.h1>
        <motion.p variants={fadeUp} className="muted hero-sub">
          Yet somehow, it does.
        </motion.p>
      </Section>

      <Section className="section bordered">
        <div className="content-sm">
          <motion.div variants={fadeUp} className="stack muted">
          <p className="statement-line">You open closed communities.</p>
          <p className="statement-line">Then a spreadsheet.</p>
          <p className="statement-line">Then another tab.</p>
          <p className="statement-line">Then three more tools.</p>
          </motion.div>

          <motion.p variants={fadeUp} className="question">
            Somewhere between Boolean strings and cold emails, you wonder:{" "}
            <span className="gradient-text italic">
              “Why is finding great leaders still this messy?”
            </span>
          </motion.p>
          <motion.p variants={fadeUp} className="muted">
            That question is exactly why <strong>Getlista</strong> exists.
          </motion.p>
        </div>
      </Section>

      {/* WE DECODE LEADERSHIP SECTION */}
      <Section className="section card">
        <div className="content-lg">
          <motion.div variants={fadeUp} className="line-accent" />

          <motion.h2 variants={fadeUp}>We don’t search profiles.</motion.h2>
          <motion.h2 variants={fadeUp} className="gradient-text mb-lg">
            We decode leadership.
          </motion.h2>

          <motion.p variants={fadeUp} className="muted max-text">
            Getlista scans the open web and not just what someone says about themselves,
            but what the ecosystem signals about them. We build layered intelligence on leaders:
          </motion.p>

          <motion.div variants={stagger} className="cards">
            {capabilities.map((c, i) => (
              <motion.div key={i} variants={fadeUp} className="card-box">
                <span className="card-index">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <h3>{c.label}</h3>
                <p className="muted">{c.desc}</p>
              </motion.div>
            ))}
          </motion.div>
          <motion.div variants={fadeUp} className="quote">
           <p className="statement-line">
              Because a title is not a capability.
              <br />
              <span className="muted">And a résumé is not a strategy.</span>
            </p>
          </motion.div>
        </div>
      </Section>

      {/* WHAT WE'RE NOT- SECTION */}
      <Section className="section">
        <div className="content-sm">
          <motion.div variants={fadeUp} className="line-accent" />

          <motion.div variants={stagger} className="strike-list">
            {[
              "We're not a job board.",

              "We're not a static database.",
              "We're not “spray and pray AI.”",
            ].map((line, i) => (
              <motion.p key={i} variants={fadeUp} className="strike">
                {line}
              </motion.p>
            ))}
          </motion.div>
          <motion.p variants={fadeUp} className="bold-close">
            We're{" "}
            <span className="gradient-text">
              structured intelligence
            </span>{" "}
            built from real-world signals.
          </motion.p>
        </div>
      </Section>

      {/* MOVE FRM SECTION */}
<Section className="section bordered">
  <div className="content-lg">

    <motion.p variants={fadeUp} className="muted small-title">
      Getlista helps executive recruiters and decision-makers move from:
    </motion.p>

    <motion.div variants={stagger} className="transition-row">

      {/* LEFT CARD */}
      <motion.div variants={fadeUp} className="transition-card muted-card">
        “This profile looks interesting…”
      </motion.div>

      {/* ARROW */}
      <motion.div variants={fadeUp} className="arrow-line" />

      {/* RIGHT CARD */}
      <motion.div variants={fadeUp} className="transition-card active-card">
        “This person can actually <span>solve our problem.</span>”
      </motion.div>

    </motion.div>

    <motion.h2 variants={fadeUp} className="faster-text">
      Faster.
    </motion.h2>

  </div>
</Section>

      {/* WHAT WE BELIEVE - SECTION */}
  <Section className="section bordered">
        <div className="content-sm">
          <motion.div variants={fadeUp} className="line-accent" />
          <motion.h2 variants={fadeUp}>What We Believe</motion.h2>

          <motion.div variants={stagger} className="beliefs">
            {beliefs.map((b, i) => (
              <motion.div key={i} variants={fadeUp} className="belief-item">
                <span className="dot" />
                <p className="muted">{b}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </Section>

      {/* CLOSING SECTION */}
      <Section className="closing bordered">
        <motion.h2 variants={fadeUp}>
          We don’t just help you <span className="gradient-text">find</span> people.
          <br />
          We help you <span className="gradient-text">understand</span> them.
        </motion.h2>

        <motion.p variants={fadeUp} className="muted">
          So when you reach out, it’s not a shot in the dark.
        </motion.p>

        <motion.p variants={fadeUp} className="closing-strong">
          It’s a <span className="gradient-text">strategic move</span>.
        </motion.p>
      </Section>
    </div>
  );
}